/**
 * WCAG contrast, so "accessibility is a launch requirement, not a polish pass" is a
 * test rather than an intention.
 *
 * The brief asks for a contrast audit of foreground tokens over the scrim, and for AA
 * minimums including over live video. A designer can audit a palette once; a test
 * audits every palette anyone ever ships, which is the difference between a launch
 * requirement and a launch-day discovery.
 */

/** Linearized channel, per WCAG's relative-luminance definition. */
function channel(value: number): number {
  const scaled = value / 255;
  return scaled <= 0.04045 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
  /** 1 unless the color carried an alpha. */
  a: number;
}

export function parseColor(value: string): Rgb | undefined {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value.trim());
  if (hex?.[1]) {
    const digits = hex[1];
    const expand = (part: string): number => Number.parseInt(part.length === 1 ? part + part : part, 16);
    if (digits.length === 3) {
      return { r: expand(digits[0] ?? ''), g: expand(digits[1] ?? ''), b: expand(digits[2] ?? ''), a: 1 };
    }
    const pairs = digits.match(/../g) ?? [];
    return {
      r: expand(pairs[0] ?? '00'),
      g: expand(pairs[1] ?? '00'),
      b: expand(pairs[2] ?? '00'),
      a: pairs[3] === undefined ? 1 : expand(pairs[3]) / 255,
    };
  }

  const rgb = /^rgba?\(([^)]+)\)$/i.exec(value.trim());
  if (rgb?.[1]) {
    const parts = rgb[1].split(',').map((part) => Number.parseFloat(part));
    if (parts.length >= 3) {
      return { r: parts[0] ?? 0, g: parts[1] ?? 0, b: parts[2] ?? 0, a: parts[3] ?? 1 };
    }
  }
  return undefined;
}

function luminance({ r, g, b }: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** A translucent color over a backdrop — how the scrim actually reaches the eye. */
function flatten(over: Rgb, onto: Rgb): Rgb {
  return {
    r: over.r * over.a + onto.r * (1 - over.a),
    g: over.g * over.a + onto.g * (1 - over.a),
    b: over.b * over.a + onto.b * (1 - over.a),
    a: 1,
  };
}

/**
 * The WCAG contrast ratio between two colors, 1–21.
 *
 * `backdrop` is what sits behind a translucent `background` — the scrim's whole purpose
 * is to be translucent, so measuring it as if it were opaque would report a contrast
 * nobody experiences. Video is unpredictable, so the honest backdrop to test the scrim
 * against is the worst case: white.
 */
export function contrastRatio(foreground: string, background: string, backdrop = '#ffffff'): number {
  const fg = parseColor(foreground);
  const bgRaw = parseColor(background);
  const behind = parseColor(backdrop);
  if (!fg || !bgRaw || !behind) throw new Error(`Cannot parse colors: ${foreground} on ${background}`);

  const bg = bgRaw.a < 1 ? flatten(bgRaw, behind) : bgRaw;
  const front = fg.a < 1 ? flatten(fg, bg) : fg;

  const lighter = Math.max(luminance(front), luminance(bg));
  const darker = Math.min(luminance(front), luminance(bg));
  return (lighter + 0.05) / (darker + 0.05);
}

/** AA: 4.5:1 for body text, 3:1 for large text (≥24px, or ≥19px bold). */
export function meetsAA(ratio: number, large = false): boolean {
  return ratio >= (large ? 3 : 4.5);
}
